import type {
  ArchetypeId,
  AxisId,
  AlternateSelf,
  SelvesState,
  LifeRecord,
  LifeGlance,
} from "./types";
import {
  ARCHETYPE_ORDER,
  AXIS_ORDER,
} from "./types";
import {
  ARCHETYPE_META,
  AXIS_LABELS,
  displayName,
  resolveInput,
} from "./options";
import { encodeState } from "./codec";
import {
  pickUniverseForArchetype,
  ARCHETYPE_PRESETS,
  conditionFromHabitability,
} from "./universes";
import { generateUniverse } from "@/lib/universe";
import {
  fnv1a,
  mulberry32,
  computeAllAxes,
} from "./scoring";
import {
  buildNarrative,
  PREMISES,
} from "./narrative";
import type { NarrativeContext } from "./narrative";
import { buildLife, buildLifeGlance } from "./narrative/life";
import { buildFallbackLife, buildMinimalLife } from "./narrative/life/fallback";
import { buildSafeNarrative } from "./narrative/safeNarrative";

export * from "./types";
export * from "./options";
export * from "./codec";
export * from "./universes";
export * from "./scoring";
export * from "./text";
export * from "./access";
export * from "./lifeChapters";
export * from "./narrative/life";

/**
 * Generate visual motif per archetype:
 * divergent -> "bloom" | "tide"
 * ascendant -> "lattice" | "orbits"
 * strange -> "rift" | "spiral"
 * unexpected -> "spiral" | "bloom" | "rift"
 */
function pickVisualMotif(
  archetype: ArchetypeId,
  rng: () => number
): "spiral" | "lattice" | "orbits" | "rift" | "bloom" | "tide" {
  switch (archetype) {
    case "divergent": {
      const pool = ["bloom", "tide", "bloom", "tide"] as const;
      return pool[Math.floor(rng() * pool.length)];
    }
    case "ascendant": {
      const pool = ["lattice", "orbits", "lattice", "orbits"] as const;
      return pool[Math.floor(rng() * pool.length)];
    }
    case "strange": {
      const pool = ["rift", "spiral", "rift", "spiral"] as const;
      return pool[Math.floor(rng() * pool.length)];
    }
    case "unexpected": {
      const pool = ["spiral", "bloom", "rift", "tide"] as const;
      return pool[Math.floor(rng() * pool.length)];
    }
  }
}

/**
 * Fingerprint representing defining semantic attributes of an alternate self.
 * [name, occupation, dossier.dilemma, story.turningPoint]
 */
export function selfFingerprint(self: AlternateSelf): string[] {
  return [
    self?.name ?? "",
    self?.occupation ?? "",
    self?.dossier?.dilemma ?? "",
    self?.story?.turningPoint ?? "",
  ];
}

/**
 * Returns true if any fingerprint item of any self in `a` equals any fingerprint item of any self in `b`.
 */
export function selvesOverlap(a: AlternateSelf[], b: AlternateSelf[]): boolean {
  if (!Array.isArray(a) || !Array.isArray(b)) {
    return false;
  }
  const itemsA = new Set<string>();
  for (const s of a) {
    if (!s) continue;
    for (const item of selfFingerprint(s)) {
      if (typeof item === "string" && item.trim().length > 0) {
        itemsA.add(item);
      }
    }
  }
  for (const s of b) {
    if (!s) continue;
    for (const item of selfFingerprint(s)) {
      if (typeof item === "string" && item.trim().length > 0 && itemsA.has(item)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Safely derives or constructs a LifeGlance preview without ever throwing.
 */
function safeBuildLifeGlance(life: LifeRecord | null | undefined): LifeGlance {
  if (life) {
    try {
      return buildLifeGlance(life);
    } catch (err) {
      console.error("buildLifeGlance threw, constructing safe glance", err);
    }
  }
  const age = typeof life?.identity?.age === "number" ? life.identity.age : 34;
  const dwelling = life?.home?.dwelling ?? "District Residence";
  const firstDwelling = dwelling.split(/[,;.\n]/)[0]?.trim() || dwelling;
  const home = firstDwelling.length > 90 ? firstDwelling.slice(0, 87).trim() + "..." : firstDwelling;

  const societySummary = life?.society?.summary ?? "A balanced civilization";
  const firstSociety = societySummary.split(/[.?!\n]/)[0]?.trim() || societySummary;
  const society = firstSociety.length > 90 ? firstSociety.slice(0, 87).trim() + "..." : firstSociety;

  const rawStanding = `${life?.standing?.wealthBracket ?? "Comfortable"} · ${life?.standing?.socialClass ?? "Respected Practitioner"}`;
  const standing = rawStanding.length > 90 ? rawStanding.slice(0, 87).trim() + "..." : rawStanding;

  return { age, home, society, standing };
}

/**
 * Generate a single AlternateSelf for a given archetype, seed, attempt count,
 * with optional forceSafe mode when retries are exhausted.
 */
function buildSingleSelf(
  archetype: ArchetypeId,
  variant: number,
  state: SelvesState,
  canonicalInputString: string,
  axes: Record<AxisId, number>,
  attempt: number,
  forceSafe = false
): AlternateSelf {
  const visitorName = displayName(state?.input);
  const selfSeed = fnv1a(`${canonicalInputString}:${archetype}:${variant}`);
  const rng = mulberry32(selfSeed ^ (attempt * 0x45d9f3b));

  // Seeded input resolution per self
  let resolved: ReturnType<typeof resolveInput>;
  try {
    const resolveRng = mulberry32(selfSeed ^ 0xa11ce ^ attempt);
    resolved = resolveInput(state?.input, resolveRng);
  } catch (err) {
    if (!forceSafe) throw err;
    const fallbackRng = mulberry32(1);
    resolved = resolveInput(
      {
        nickname: "",
        interests: [],
        strengths: [],
        ambition: null,
        lifePath: null,
      },
      fallbackRng
    );
  }

  // Universe assignment
  let config: any;
  let profile: any;
  let condition: any;
  try {
    const univ = pickUniverseForArchetype(
      archetype,
      selfSeed ^ attempt,
      state?.universe ?? null
    );
    config = univ.config;
    profile = univ.profile;
    condition = univ.condition;
  } catch (err) {
    if (!forceSafe) throw err;
    try {
      const fallbackUniv = pickUniverseForArchetype(archetype, 1, null);
      config = fallbackUniv.config;
      profile = fallbackUniv.profile;
      condition = fallbackUniv.condition;
    } catch {
      throw err;
    }
  }

  // Seeded independent stream factory
  const stream = (label: string) => {
    const streamSeed = fnv1a(`${selfSeed}:${attempt}:${label}`);
    return mulberry32(streamSeed);
  };

  const narrativeRng = mulberry32(selfSeed ^ 0xdeadbeef ^ attempt);

  const ctx: NarrativeContext = {
    archetype,
    input: state?.input ?? {
      nickname: "",
      interests: [],
      strengths: [],
      ambition: null,
      lifePath: null,
    },
    name: visitorName,
    variant,
    seed: selfSeed,
    rng: narrativeRng,
    stream,
    attempt,
    resolved,
    axes,
    condition,
    universe: {
      name: profile?.name ?? "Cosmos",
      code: profile?.code ?? "U-1",
      config,
      profile,
    },
    sharedUniverse: state?.universe !== null && state?.universe !== undefined,
  };

  let narrativeResult: any;
  if (forceSafe) {
    try {
      narrativeResult = buildNarrative(ctx);
    } catch {
      narrativeResult = buildSafeNarrative(ctx);
    }
  } else if (attempt >= 8) {
    try {
      narrativeResult = buildNarrative(ctx);
    } catch (err) {
      console.error("buildNarrative failed, using safe story", archetype, attempt, err);
      narrativeResult = buildSafeNarrative(ctx);
    }
  } else {
    narrativeResult = buildNarrative(ctx);
  }

  const dossier = narrativeResult.dossier;

  const hashHex = (selfSeed >>> 0).toString(16).toUpperCase().padStart(8, "0").slice(0, 6);
  const archetypeIndex = ARCHETYPE_ORDER.indexOf(archetype);
  const timelineId = `TL-${hashHex}-Δ${archetypeIndex + 1}${attempt > 0 ? `r${attempt}` : ""}`;

  const hue = Math.floor(rng() * 360);
  const hue2 = (hue + 60 + Math.floor(rng() * 180)) % 360;
  const motif = pickVisualMotif(archetype, rng);

  let life: LifeRecord;
  let lifeGlance: LifeGlance;

  if (forceSafe) {
    try {
      life = buildFallbackLife(ctx, narrativeResult);
    } catch {
      life = buildMinimalLife(ctx, narrativeResult);
    }
    lifeGlance = safeBuildLifeGlance(life);
  } else {
    try {
      life = buildLife(ctx, narrativeResult);
      lifeGlance = safeBuildLifeGlance(life);
    } catch (err) {
      console.error("buildLife failed", archetype, attempt, err);
      if (attempt < 8) {
        throw err;
      }
      try {
        life = buildFallbackLife(ctx, narrativeResult);
        lifeGlance = safeBuildLifeGlance(life);
      } catch (fallbackErr) {
        console.error("buildFallbackLife failed, using minimal life", archetype, attempt, fallbackErr);
        life = buildMinimalLife(ctx, narrativeResult);
        lifeGlance = safeBuildLifeGlance(life);
      }
    }
  }

  return {
    archetype,
    scenarioLabel: ARCHETYPE_META[archetype]?.label ?? "An Alternate Reality",
    name: narrativeResult.name,
    title: narrativeResult.title,
    timelineId,
    tagline: narrativeResult.tagline,
    occupation: narrativeResult.occupation,
    traits: narrativeResult.traits,
    axes,
    story: narrativeResult.story,
    choices: narrativeResult.choices,
    worldDetails: narrativeResult.worldDetails,
    dossier,
    resolved,
    universe: {
      name: profile?.name ?? "Cosmos",
      code: profile?.code ?? "U-1",
      description: narrativeResult.universeDescription,
      condition,
      config,
      profile,
    },
    differences: {
      summary: "",
      versus: [],
    },
    visual: {
      seed: selfSeed,
      hue,
      hue2,
      motif,
    },
    lifeGlance,
    life,
  };
}

/**
 * Generate the 4 fictional alternate selves deterministically from state.
 */
export function generateSelves(state: SelvesState): AlternateSelf[] {
  let canonicalInputString: string;
  try {
    canonicalInputString = encodeState({
      input: state?.input ?? {
        nickname: "",
        interests: [],
        strengths: [],
        ambition: null,
        lifePath: null,
      },
      variants: [0, 0, 0, 0],
      universe: null,
    });
  } catch (err) {
    console.error("encodeState failed in generateSelves", err);
    canonicalInputString = "v1-default";
  }

  const baseInputSeed = fnv1a(canonicalInputString);
  let allAxes: Record<ArchetypeId, Record<AxisId, number>>;
  try {
    allAxes = computeAllAxes(state?.input, state?.variants, baseInputSeed);
  } catch (err) {
    console.error("computeAllAxes failed in generateSelves", err);
    const fallbackAxes: Record<AxisId, number> = {
      curiosity: 50,
      connection: 50,
      courage: 50,
      stability: 50,
      influence: 50,
    };
    allAxes = {
      divergent: { ...fallbackAxes },
      ascendant: { ...fallbackAxes },
      strange: { ...fallbackAxes },
      unexpected: { ...fallbackAxes },
    };
  }

  const selves: AlternateSelf[] = [];

  // Generate selves with collision detection among the four
  for (let i = 0; i < ARCHETYPE_ORDER.length; i++) {
    const archetype = ARCHETYPE_ORDER[i];
    const variant = state?.variants?.[i] ?? 0;
    const axes = allAxes[archetype] ?? {
      curiosity: 50,
      connection: 50,
      courage: 50,
      stability: 50,
      influence: 50,
    };

    let currentSelf: AlternateSelf | null = null;

    // Collision detection and error recovery against prior selves generated in this run
    let attempt = 0;
    while (attempt < 12) {
      try {
        currentSelf = buildSingleSelf(archetype, variant, state, canonicalInputString, axes, attempt);
        let collided = false;
        for (const prior of selves) {
          if (!prior || !currentSelf) continue;
          if (
            (currentSelf.name && currentSelf.name === prior.name) ||
            (currentSelf.occupation && currentSelf.occupation === prior.occupation) ||
            (currentSelf.dossier?.dilemma && currentSelf.dossier.dilemma === prior.dossier?.dilemma) ||
            (currentSelf.story?.turningPoint && currentSelf.story.turningPoint === prior.story?.turningPoint)
          ) {
            collided = true;
            break;
          }
        }
        if (!collided || attempt === 11) {
          // If collision occurred on the final attempt (11), keep currentSelf as fallback
          break;
        }
      } catch (err) {
        console.error("buildSingleSelf failed", archetype, attempt, err);
      }
      attempt++;
    }

    if (!currentSelf) {
      console.error("All 12 buildSingleSelf attempts failed, building guaranteed safe self for", archetype);
      try {
        currentSelf = buildSingleSelf(archetype, variant, state, canonicalInputString, axes, 12, true);
      } catch (safeErr) {
        console.error("Critical: forced safe self generation threw", archetype, safeErr);
      }
    }

    if (!currentSelf) {
      // Last-ditch emergency self object if even buildSingleSelf forceSafe threw
      const safeSeed = fnv1a(`${canonicalInputString}:${archetype}:${variant}`);
      const resolveRng = mulberry32(safeSeed);
      const fallbackResolved = resolveInput(
        state?.input ?? {
          nickname: "",
          interests: [],
          strengths: [],
          ambition: null,
          lifePath: null,
        },
        resolveRng
      );
      const safeUniv = ARCHETYPE_PRESETS[archetype] ?? ARCHETYPE_PRESETS.divergent;
      const safeProfile = generateUniverse(safeUniv);
      const safeCtx: NarrativeContext = {
        archetype,
        input: state?.input ?? {
          nickname: "",
          interests: [],
          strengths: [],
          ambition: null,
          lifePath: null,
        },
        name: displayName(state?.input),
        variant,
        seed: safeSeed,
        rng: mulberry32(safeSeed),
        stream: (label: string) => mulberry32(fnv1a(`${safeSeed}:${label}`)),
        attempt: 12,
        resolved: fallbackResolved,
        axes,
        condition: conditionFromHabitability(safeProfile.habitability),
        universe: {
          name: safeProfile.name,
          code: safeProfile.code,
          config: safeUniv,
          profile: safeProfile,
        },
        sharedUniverse: state?.universe !== null && state?.universe !== undefined,
      };
      const safeNarrative = buildSafeNarrative(safeCtx);
      const safeLife = buildMinimalLife(safeCtx, safeNarrative);
      const safeGlance = safeBuildLifeGlance(safeLife);
      currentSelf = {
        archetype,
        scenarioLabel: ARCHETYPE_META[archetype]?.label ?? "An Alternate Reality",
        name: safeNarrative.name,
        title: safeNarrative.title,
        timelineId: `TL-${(safeSeed >>> 0).toString(16).toUpperCase().padStart(8, "0").slice(0, 6)}-Δ${i + 1}`,
        tagline: safeNarrative.tagline,
        occupation: safeNarrative.occupation,
        traits: safeNarrative.traits,
        axes,
        story: safeNarrative.story,
        choices: safeNarrative.choices,
        worldDetails: safeNarrative.worldDetails,
        dossier: safeNarrative.dossier,
        resolved: fallbackResolved,
        universe: {
          name: safeProfile.name,
          code: safeProfile.code,
          description: safeNarrative.universeDescription,
          condition: conditionFromHabitability(safeProfile.habitability),
          config: safeUniv,
          profile: safeProfile,
        },
        differences: {
          summary: "",
          versus: [],
        },
        visual: {
          seed: safeSeed,
          hue: 180,
          hue2: 240,
          motif: "bloom",
        },
        lifeGlance: safeGlance,
        life: safeLife,
      };
    }

    selves.push(currentSelf);
  }

  // Build differences summary and versus text for each self
  for (let i = 0; i < selves.length; i++) {
    const current = selves[i];
    if (!current) continue;
    const others = selves.filter((_, idx) => idx !== i && Boolean(_));

    try {
      const axisDifferences = AXIS_ORDER.map((axis) => {
        const otherScores = others.map((o) => o?.axes?.[axis] ?? 50);
        const otherMean =
          otherScores.length > 0
            ? otherScores.reduce((sum, val) => sum + val, 0) / otherScores.length
            : 50;
        const currentScore = current?.axes?.[axis] ?? 50;
        return {
          axis,
          delta: currentScore - otherMean,
          absDelta: Math.abs(currentScore - otherMean),
        };
      }).sort((a, b) => b.absDelta - a.absDelta);

      const topAxis = axisDifferences[0] ?? { axis: "curiosity", delta: 0, absDelta: 0 };
      const topAxisLabel = (AXIS_LABELS[topAxis.axis] || topAxis.axis).toLowerCase();
      const roundedDelta = Math.round(topAxis.delta);
      const axisSentence =
        roundedDelta >= 0
          ? `In personality geometry, this timeline displays a noticeably stronger ${topAxisLabel} than your other versions (+${roundedDelta}).`
          : `In personality geometry, this timeline displays a noticeably weaker ${topAxisLabel} than your other versions (${roundedDelta}).`;

      const premiseText = PREMISES[current.archetype] || ARCHETYPE_META[current.archetype]?.blurb || "";
      const currentOcc = (current.occupation ?? "wanderer").toLowerCase();
      const currentDilemma = current.dossier?.dilemma ?? "";
      const summary = `The premise of this timeline: ${premiseText} ${axisSentence} Here, you live as ${currentOcc}. Defining dilemma: ${currentDilemma}`;

      const versus = others.map((other) => {
        let maxDiffAxis: AxisId = "curiosity";
        let maxDelta = 0;
        for (const ax of AXIS_ORDER) {
          const diff = Math.abs((current.axes?.[ax] ?? 50) - (other?.axes?.[ax] ?? 50));
          if (diff > maxDelta) {
            maxDelta = diff;
            maxDiffAxis = ax;
          }
        }

        const currentScore = current.axes?.[maxDiffAxis] ?? 50;
        const otherScore = other?.axes?.[maxDiffAxis] ?? 50;
        const comparisonWord = currentScore >= otherScore ? "higher" : "lower";
        const maxDiffAxisLabel = (AXIS_LABELS[maxDiffAxis] || maxDiffAxis).toLowerCase();

        const trimPeriod = (s: string) => (s ?? "").replace(/\.+$/, "");
        const otherDilemma = other?.dossier?.dilemma ?? "";
        const currentDil = current?.dossier?.dilemma ?? "";
        const otherName = other?.name ?? "Other Self";
        const currentName = current?.name ?? "Traveler";
        const dilemmaContrast =
          currentDil && otherDilemma
            ? ` ${otherName} wrestles with: "${trimPeriod(otherDilemma)}" — whereas ${currentName} faces: "${trimPeriod(currentDil)}"`
            : "";

        const otherOcc = (other?.occupation ?? "wanderer").toLowerCase();
        const otherUnivName = other?.universe?.name ?? "alternate reality";
        const otherScenario = other?.scenarioLabel ?? "An Alternate Reality";
        const text = `Compared to ${otherName} (${otherScenario}), ${currentName} displays a clearly ${comparisonWord} ${maxDiffAxisLabel} (${currentScore} vs ${otherScore}). While ${otherName} operates as a ${otherOcc} in "${otherUnivName}", this version charts life as a ${currentOcc}.${dilemmaContrast}`;

        return {
          other: other.archetype,
          text,
        };
      });

      current.differences = {
        summary,
        versus,
      };
    } catch (diffErr) {
      console.error("Failed to calculate differences, using safe fallback", diffErr);
      current.differences = {
        summary: `A distinct timeline shaped by unique cosmological and life-path choices.`,
        versus: others.map((o) => ({
          other: o.archetype,
          text: `Compared to ${o?.name ?? "Other Self"}, this timeline reflects a distinct divergence in experience and values.`,
        })),
      };
    }
  }

  return selves;
}
